import React, { useCallback } from "react";
import { Box } from "@mui/material";
import {
  embeddedAgentPanelBaseSx,
  embeddedAgentPanelSx,
} from "../pages/ScopedAgentChatPanel";
import { PAGE_HEADER_HEIGHT } from "../../constants/pageLayout";
import useUIPreferenceStore from "../../stores/useUIPreferenceStore";
import { useContextAgent } from "./ContextAgentContext";
import useFloatingDrag from "./useFloatingDrag";
import { getWidgetPanelHeight, getWidgetPanelWidth } from "./widgetAnchor";
import "./ContextAgentWidgetPanel.css";

const widgetAgentPanelBaseSx = {
  position: "fixed",
  maxWidth: "calc(100vw - 48px)",
  borderRadius: "12px",
  overflow: "hidden",
  boxShadow: (theme) =>
    theme.palette.mode === "dark"
      ? "0 16px 48px rgba(0, 0, 0, 0.45)"
      : "0 16px 48px rgba(15, 23, 42, 0.18)",
  border: (theme) => `1px solid ${theme.palette.divider}`,
  zIndex: 1310,
  flexShrink: 0,
};

export default function ContextAgentPanelHost() {
  const assistantLayout = useUIPreferenceStore((s) => s.assistantLayout);
  const {
    registration,
    open,
    setOpen,
    isRegistered,
    widgetPanelAnchor,
    setWidgetPanelAnchor,
    widgetPanelDragRef,
    widgetTaskQueueOpen,
  } = useContextAgent();

  const handlePanelMove = useCallback(
    (next) => {
      setWidgetPanelAnchor(next);
    },
    [setWidgetPanelAnchor]
  );

  const {
    targetRef: panelRef,
    isDragging,
    handlePointerDown,
    handlePointerMove,
    finishPointerInteraction,
  } = useFloatingDrag({
    onMove: handlePanelMove,
    dragZoneHeight: PAGE_HEADER_HEIGHT,
    disabled: assistantLayout !== "widget" || !open,
  });

  const panel = registration?.panel;

  if (!isRegistered || !open || !panel || !widgetPanelAnchor) {
    if (widgetPanelDragRef) {
      widgetPanelDragRef.current = null;
    }
    return null;
  }

  const panelContent =
    typeof panel === "function" ? panel({ onClose: () => setOpen(false) }) : panel;

  if (assistantLayout === "widget" && widgetPanelDragRef) {
    widgetPanelDragRef.current = {
      handlePointerDown,
      isDragging,
    };
  } else if (widgetPanelDragRef) {
    widgetPanelDragRef.current = null;
  }

  const panelHeight = getWidgetPanelHeight();
  const widgetPanelWidth = getWidgetPanelWidth(widgetTaskQueueOpen);

  const hostSx =
    assistantLayout === "widget"
      ? {
          ...embeddedAgentPanelBaseSx,
          ...widgetAgentPanelBaseSx,
          left: widgetPanelAnchor.x,
          top: widgetPanelAnchor.y,
          width: widgetPanelWidth,
          height: panelHeight,
          maxHeight: panelHeight,
          transition: isDragging ? "none" : "width 0.18s ease",
        }
      : embeddedAgentPanelSx;

  if (assistantLayout !== "widget") {
    return (
      <Box sx={hostSx} data-testid="context-agent-panel-host" data-assistant-layout={assistantLayout}>
        {panelContent}
      </Box>
    );
  }

  return (
    <Box
      ref={panelRef}
      sx={hostSx}
      className={`context-agent-widget-panel${isDragging ? " is-dragging" : ""}`}
      data-testid="context-agent-panel-host"
      data-assistant-layout={assistantLayout}
      onPointerMove={handlePointerMove}
      onPointerUp={finishPointerInteraction}
      onPointerCancel={finishPointerInteraction}
    >
      {panelContent}
    </Box>
  );
}
